import React, { useState, useEffect, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NoIndex from "../NoIndex";
import MyContext from "../../MyContext";

const TemplateView = ({ userID }) => {
  const { baseUrl, config } = useContext(MyContext);
  const navigate = useNavigate();
  const location = useLocation();

  // DATA & UI STATE
  const [templates, setTemplates] = useState({});
  const [templateIndex, setTemplateIndex] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [details, setDetails] = useState(false);

  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [error, setError] = useState("");

  // READ QUERY PARAMS
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("templateId");
    const name = params.get("templateName");

    setTemplateIndex(id || "");
    setTemplateName(name || "");
    setDetails(Boolean(id || name));
  }, [location.search]);

  const handleEditClick = () => {
    if (!templateIndex) return;
    navigate(`/WhatsApp/Template/TemplateEdit?templateID=${templateIndex}`);
  };

  // FETCH DETAILS
  const getTemplateDetail = async () => {
    if (!templateName) {
      setTemplates({});
      return;
    }
    setLoading(true);
    setError("");
    try {
      const body = JSON.stringify({
        userid: userID,
        secret: "string",
        templateId: templateName,
      });

      const res = await fetch(`${baseUrl}/api/Whatsapp/getTemplateDetails`, {
        method: "POST",
        headers: config.headers,
        body,
      });

      if (!res.ok) throw new Error("Network error");
      const data = await res.json();

      if (data?.responseCode === "00" && data?.response) {
        setTemplates(data.response);
      } else {
        setTemplates({});
        setError(data?.message || "Failed to load template details.");
      }
    } catch (e) {
      setTemplates({});
      setError(e?.message || "Something went wrong.");
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  };

  useEffect(() => {
    getTemplateDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateName]);

  // SAFE ACCESSORS
  const components = useMemo(() => templates?.components || [], [templates]);
  const headerComp = useMemo(
    () => components.find((c) => c?.type === "HEADER") || {},
    [components]
  );
  const bodyComp = useMemo(
    () => components.find((c) => c?.type === "BODY") || {},
    [components]
  );
  const footerComp = useMemo(
    () => components.find((c) => c?.type === "FOOTER") || {},
    [components]
  );
  const btnComp = useMemo(
    () => components.find((c) => c?.type === "BUTTONS") || { buttons: [] },
    [components]
  );

  const videoHeader = useMemo(
    () =>
      components.find((c) => c?.format === "VIDEO")?.example?.header_handle?.[0],
    [components]
  );
  const imageHeader = useMemo(
    () =>
      components.find((c) => c?.format === "IMAGE")?.example?.header_handle?.[0],
    [components]
  );

  // FULL-SCREEN STYLES
  const pageWrap = {
    // width: "100vw",
    height: "100vh",
    background: "#f6f7fb",
    fontFamily: "Nunito, sans-serif",
    overflow: "hidden",
  };
  const shell = {
    width: "100%",
    height: "100%",
    padding: 0,
    margin: 0,
  };
  const card = {
    // width: "100%",
    height: "100%",
    background: "#fff",
    borderRadius: 0, // full-bleed
    boxShadow: "0 0 0 rgba(0,0,0,0)", // no outer shadow in full-screen
    display: "flex",
    flexDirection: "column",
  };
  const headerBar = {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid #eef0f3",
    background: "#fff",
  };
  const title = { margin: 0, fontSize: 20, fontWeight: 800 };
  const sub = { marginLeft: 10, color: "#6c757d", fontSize: 13 };
  const chip = (bg = "#eef7ff", color = "#0d6efd") => ({
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: bg,
    color,
    border: `1px solid ${color}1a`,
  });
  const btn = (bg, border, color = "#fff") => ({
    padding: "8px 12px",
    borderRadius: 8,
    border: `1px solid ${border}`,
    background: bg,
    color,
    cursor: "pointer",
  });

  const bodyArea = {
    flex: "1 1 auto",
    overflow: "auto",
    padding: 20,
    background: "#f6f7fb",
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
    minHeight: "calc(100% - 0px)",
  };

  const section = {
    background: "#fff",
    border: "1px solid #eef0f3",
    borderRadius: 10,
    overflow: "hidden",
  };
  const sectionBody = { padding: 16 };
  const kvRow = {
    display: "grid",
    gridTemplateColumns: "220px 1fr",
    gap: 12,
    alignItems: "start",
    padding: "10px 16px",
    borderBottom: "1px dashed #edf0f4",
  };
  const label = { color: "#6c757d", fontWeight: 700, fontSize: 13 };
  const value = { color: "#212529", fontSize: 14, wordBreak: "break-word" };
  const skeleton = {
    background: "linear-gradient(90deg,#f2f3f7,#eef1f6,#f2f3f7)",
    height: 14,
    borderRadius: 6,
    animation: "pulse 1.2s ease-in-out infinite",
  };

  return (
    <>
      <NoIndex />
      {/* keyframes for spinner + skeleton */}
      <style>{`
        @media (max-width: 1200px){
          /* collapse to single column on smaller viewports */
          .tv-grid { grid-template-columns: 1fr !important; }
          .tv-kv    { grid-template-columns: 160px 1fr !important; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0% { background-position: -100px 0; }
          100% { background-position: 200px 0; }
        }
      `}</style>

      <div style={pageWrap}>
        <div style={shell}>
          <div style={card}>
            {/* FULL-WIDTH HEADER */}
            <div style={headerBar}>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                <h1 style={title}>Template Details</h1>
                {templates?.name ? (
                  <span style={sub}>({templates.name})</span>
                ) : templateName ? (
                  <span style={sub}>({templateName})</span>
                ) : null}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {templates?.status && (
                  <span style={chip("#effaf4", "#198754")}>
                    {String(templates.status).toUpperCase()}
                  </span>
                )}
                {templates?.category && (
                  <span style={chip("#fff7e6", "#fd7e14")}>{templates.category}</span>
                )}
                <button
                  style={btn("#fff", "#ced4da", "#212529")}
                  onClick={() => navigate(-1)}
                >
                  ← Back
                </button>
                <button
                  style={btn("#0d6efd", "#0d6efd")}
                  onClick={getTemplateDetail}
                  disabled={loading}
                  title="Refresh"
                >
                  {loading ? "Refreshing…" : "Refresh"}
                </button>
                
              </div>
            </div>

            {/* SCROLLABLE BODY (FULL HEIGHT) */}
            <div style={bodyArea}>
              {/* LOADER / ERROR / EMPTY */}
              {loading && firstLoad && (
                <div className="tv-grid" style={{ ...grid }}>
                  <div style={{ ...section, padding: 16 }}>
                    {[...Array(10)].map((_, i) => (
                      <div key={i} style={{ marginBottom: 12 }}>
                        <div style={{ ...skeleton, width: 160 }} />
                        <div
                          style={{
                            ...skeleton,
                            width: i % 2 ? "60%" : "85%",
                            height: 18,
                            marginTop: 8,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ ...section, minHeight: 420 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          border: "3px solid #e9ecef",
                          borderTopColor: "#0d6efd",
                          animation: "spin .8s linear infinite",
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!loading && error && (
                <div
                  className="alert alert-danger"
                  role="alert"
                  style={{ borderRadius: 10 }}
                >
                  {error}
                </div>
              )}

              {!loading && !error && details && !templates?.name && (
                <div
                  className="alert alert-warning"
                  role="alert"
                  style={{ borderRadius: 10 }}
                >
                  No data found for <b>{templateName}</b>.
                </div>
              )}

              {/* CONTENT */}
              {!firstLoad && !loading && !error && templates?.name && (
                <div className="tv-grid" style={grid}>
                  {/* LEFT: DETAILS */}
                  <div style={section}>
                    <div style={sectionBody}>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Template ID</div>
                        <div style={value}>{templates?.id || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Template Name</div>
                        <div style={value}>{templates?.name || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Language</div>
                        <div style={value}>{templates?.language || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Status</div>
                        <div style={value}>
                          <span style={chip("#effaf4", "#198754")}>
                            {templates?.status || "—"}
                          </span>
                        </div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Category</div>
                        <div style={value}>{templates?.category || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Header Type</div>
                        <div style={value}>{headerComp?.format || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Header Text</div>
                        <div style={value}>{headerComp?.text || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Body Type</div>
                        <div style={value}>{bodyComp?.format || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Body Text</div>
                        <div style={value}>{bodyComp?.text || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Footer Text</div>
                        <div style={value}>{footerComp?.text || "—"}</div>
                      </div>
                      <div className="tv-kv" style={kvRow}>
                        <div style={label}>Buttons</div>
                        <div style={value}>
                          {Array.isArray(btnComp?.buttons) &&
                          btnComp.buttons.length > 0 ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                              {btnComp.buttons.map((b, i) => (
                                <a
                                  key={i}
                                  href={b?.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={chip("#eef7ff", "#0d6efd")}
                                >
                                  {b?.text || "Button"}
                                </a>
                              ))}
                            </div>
                          ) : (
                            "No Buttons"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: (UNCHANGED) MOBILE PREVIEW */}
                  <div style={{ ...section, padding: 16 }}>
                    {/* ---- your original mobile block preserved ---- */}
                    <div className="mobile-card" style={{ position: "relative" }}>
                      <div className="mobile-btn1"></div>
                      <div className="mobile-btn2"></div>
                      <div className="mobile-btn3"></div>
                      <div className="mobile-btn4"></div>

                      <div className="mobile-card-int">
                        <div className="bubble-container">
                          <div className="message-bubble message-bubble-animate">
                            {videoHeader ? (
                              <video width="90%" height="200px" controls>
                                <source src={videoHeader} type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            ) : imageHeader ? (
                              <img
                                src={imageHeader}
                                alt="Template Image"
                                style={{ maxWidth: "90%", borderRadius: "8px" }}
                              />
                            ) : null}

                            <div className="header-text">{headerComp?.text || ""}</div>

                            <div className="body-text py-1">
                              <div>{bodyComp?.text || ""}</div>
                            </div>

                            <div className="footer-text">{footerComp?.text || ""}</div>

                            <div className="footer-text text-end">05:27 PM</div>
                          </div>

                          {Array.isArray(btnComp?.buttons) &&
                            btnComp.buttons.map((btn, index) => (
                              <div
                                key={index}
                                className="button-bubble button-bubble-animate text-center"
                              >
                                <a
                                  href={btn?.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="button-text text-success text-center mb-1"
                                >
                                  {btn?.text}
                                </a>
                              </div>
                            ))}
                        </div>
                      </div>

                      <div className="mobile-top">
                        <div className="mobile-camera">
                          <div className="mobile-int"></div>
                        </div>
                        <div className="mobile-speaker"></div>
                      </div>
                    </div>
                    {/* ---- end mobile block ---- */}
                  </div>
                </div>
              )}
            </div>
            {/* /bodyArea */}
          </div>
        </div>
      </div>
    </>
  );
};

export default TemplateView;
